/**
 * RetailerUsers (/retaileradmin/users) — managers only.
 *
 * The retailer's own registration queue and team, limited to what the signed-in
 * manager may manage: every row on the nodes they hold whole (sub-retailers
 * included when they hold them), otherwise the rows on their node that share
 * one of their locations plus registrants with no location yet. One line per
 * person; Access says in words what they see, and Edit opens the same ticks
 * Trusscore uses, limited to what this manager may hand out.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, UserCheck, UserX, Loader2, Mail, Link2, Building2, Cloud, Pencil } from 'lucide-react';
import { useRetailerContext } from './RetailerAdminContext';
import { RetailerAccessEditor } from '../../components/retaileradmin/RetailerAccessEditor';
import { ConfirmModal } from '../../components/admin/ConfirmModal';
import {
  fetchRetailerMembers,
  retailerMemberAction,
  type RetailerMemberRow,
  type MemberStatus,
  type RetailerRole,
  type RetailerAssignable,
} from '../../services/retailerAdminApi';
import { formatDate } from '../../utils/estimateLabels';

type Tab = MemberStatus;

const SourceIcon: React.FC<{ m: RetailerMemberRow }> = ({ m }) => {
  if (m.invited) return <Link2 size={13} />;
  if (m.source.startsWith('salesforce')) return <Cloud size={13} />;
  if (m.source === 'domain') return <Mail size={13} />;
  return <Building2 size={13} />;
};

interface Group { userId: number; rows: RetailerMemberRow[] }

export const RetailerUsers: React.FC = () => {
  const { t, i18n } = useTranslation('retaileradmin');
  const ctx = useRetailerContext();
  const [members, setMembers] = useState<RetailerMemberRow[]>([]);
  const [counts, setCounts] = useState<Record<MemberStatus, number>>({ pending: 0, approved: 0, rejected: 0 });
  const [assignable, setAssignable] = useState<RetailerAssignable>({ nodes: [], locations: [] });
  const [tab, setTab] = useState<Tab>('pending');
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [notice, setNotice] = useState<{ kind: 'danger' | 'success'; text: string } | null>(null);
  const [editing, setEditing] = useState<Group | null>(null);
  const [removing, setRemoving] = useState<Group | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const d = await fetchRetailerMembers();
      setMembers(d.members);
      setCounts(d.counts);
      setAssignable(d.assignable ?? { nodes: [], locations: d.locations.map(l => ({ ...l, retailerId: ctx.retailerId, retailerName: ctx.retailerName })) });
      setTab(prev => (prev === 'pending' && d.counts.pending === 0 ? 'approved' : prev));
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [ctx.retailerId, ctx.retailerName]);

  useEffect(() => { load(); }, [load, ctx.retailerId]);

  const describeError = useCallback((code: string) => {
    if (code === 'last_manager') return t('users.errors.lastManager');
    if (code === 'self_row') return t('users.errors.selfRow');
    if (code === 'all_locations_row' || code === 'all_locations_only' || code === 'outside_scope') return t('users.errors.allLocationsOnly');
    if (code === 'nothing_picked') return t('users.errors.nothingPicked');
    if (code === 'not_pending' || code === 'not_open' || code === 'not_approved' || code === 'not_found') return t('users.errors.stale');
    return t('users.errors.failed');
  }, [t]);

  const run = async (id: number, fn: () => Promise<unknown>, successText?: string) => {
    setBusyId(id);
    setNotice(null);
    try {
      await fn();
      if (successText) setNotice({ kind: 'success', text: successText });
      await load();
    } catch (e) {
      const code = e instanceof Error ? e.message : '';
      setNotice({ kind: 'danger', text: describeError(code) });
      if (code === 'not_pending' || code === 'not_open' || code === 'not_approved' || code === 'not_found') load();
    } finally {
      setBusyId(null);
    }
  };

  const nodeName = (id: number) => assignable.nodes.find(n => n.id === id)?.name ?? assignable.locations.find(l => l.retailerId === id)?.retailerName ?? (id === ctx.retailerId ? ctx.retailerName : `#${id}`);

  const accessText = (r: RetailerMemberRow): string => {
    if (r.status !== 'approved') return '';
    if (r.allLocations) return r.includeChildren ? t('users.access.summaryEntireWithChildren', { name: nodeName(r.retailerId) }) : t('users.access.summaryEntire', { name: nodeName(r.retailerId) });
    if (r.locations.length) return t('users.access.summaryLocations', { name: nodeName(r.retailerId), locations: r.locations.map(l => l.name).join(', ') });
    return t('locations.scope.none');
  };

  /** One line per person in the active tab. */
  const groups = useMemo<Group[]>(() => {
    const out: Group[] = []; const idx = new Map<number, number>();
    for (const m of members.filter(x => x.status === tab)) {
      const i = idx.get(m.userId);
      if (i === undefined) { idx.set(m.userId, out.length); out.push({ userId: m.userId, rows: [m] }); } else out[i].rows.push(m);
    }
    return out;
  }, [members, tab]);

  const tabs: Tab[] = ['pending', 'approved', 'rejected'];

  return (
    <div className="bg-white rounded-3 shadow-sm p-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div>
          <h2 className="fw-bold mb-1 d-flex align-items-center gap-2"><Users size={22} /> {t('users.title')}</h2>
          <p className="text-muted mb-0 small">
            {ctx.allLocations
              ? t('users.subtitle', { retailer: ctx.retailerName })
              : t('users.subtitleLocations', { locations: ctx.locations.map(l => l.name).join(', ') })}
          </p>
        </div>
      </div>

      <ul className="nav nav-pills gap-2 mb-3">
        {tabs.map(s => (
          <li key={s} className="nav-item">
            <button type="button" className={`nav-link d-inline-flex align-items-center gap-2 ${tab === s ? 'active' : ''}`} onClick={() => setTab(s)}>
              {t(`users.tabs.${s}`)}
              <span className={`badge ${s === 'pending' && counts[s] > 0 ? 'bg-warning text-dark' : 'bg-secondary'}`}>{counts[s]}</span>
            </button>
          </li>
        ))}
      </ul>

      {notice && <div className={`alert alert-${notice.kind} py-2 small`}>{notice.text}</div>}

      {loading ? (
        <div className="text-center py-4"><Loader2 size={20} className="text-muted" style={{ animation: 'spin 1s linear infinite' }} /></div>
      ) : failed ? (
        <p className="text-danger small mb-0">{t('users.loadFailed')}</p>
      ) : groups.length === 0 ? (
        <p className="text-muted small mb-0">{t(`users.empty.${tab}`)}</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead>
              <tr className="text-muted small">
                <th>{t('users.columns.person')}</th>
                <th className="d-none d-md-table-cell">{t('users.columns.matched')}</th>
                {tab === 'approved' && <th>{t('users.columns.role')}</th>}
                {tab !== 'rejected' && <th>{t('users.columns.access')}</th>}
                {tab !== 'pending' && <th className="d-none d-lg-table-cell">{t('users.columns.decidedBy')}</th>}
                <th className="text-end">{t('users.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(g => {
                const m = g.rows[0];
                const busy = g.rows.some(r => r.id === busyId);
                const name = [m.firstName, m.lastName].filter(Boolean).join(' ');
                const role: RetailerRole = g.rows.some(r => r.role === 'manager') ? 'manager' : 'viewer';
                const locked = busy || m.isSelf;
                return (
                  <tr key={g.userId}>
                    <td>
                      <div className="fw-semibold small">
                        {name || <span className="text-muted fst-italic">{t('users.noName')}</span>}
                        {m.isSelf && <span className="badge bg-light text-dark border ms-2">{t('users.you')}</span>}
                      </div>
                      <div className="text-muted" style={{ fontSize: '0.8em' }}>{m.email}</div>
                      <div className="text-muted d-none d-sm-block" style={{ fontSize: '0.75em' }}>{t('users.columns.registered')} {formatDate(m.createdAt, i18n.language)}</div>
                    </td>
                    <td className="small d-none d-md-table-cell">
                      <span className="d-inline-flex align-items-center gap-1 text-muted">
                        <SourceIcon m={m} />
                        {m.invited ? t('users.sources.invite')
                          : m.source.startsWith('salesforce') ? t('users.sources.salesforce')
                          : m.source === 'domain' ? t('users.sources.domain', { domain: m.matchedDomain ?? '' })
                          : t('users.sources.other')}
                      </span>
                    </td>
                    {tab === 'approved' && (
                      <td>
                        <select
                          className="form-select form-select-sm"
                          style={{ width: 150 }}
                          value={role}
                          disabled={locked}
                          aria-label={t('users.columns.role')}
                          title={t(`users.roleHelp.${role}`)}
                          onChange={e => run(m.id, async () => { for (const r of g.rows) await retailerMemberAction({ action: 'set_role', id: r.id, role: e.target.value as RetailerRole }); }, t('users.roleSaved'))}
                        >
                          <option value="viewer">{t('roles.viewer')}</option>
                          <option value="manager">{t('roles.manager')}</option>
                        </select>
                        <div className="text-muted" style={{ fontSize: '0.75em' }}>{t(`users.roleSource.${m.roleSource}`)}</div>
                      </td>
                    )}
                    {tab !== 'rejected' && (
                      <td className="small" style={{ minWidth: 220 }}>
                        {tab === 'approved' ? (
                          <>
                            {g.rows.map(r => <div key={r.id} className={r.scope === 'none' ? 'text-danger' : ''}>{accessText(r)}</div>)}
                            {!m.isSelf && <button type="button" className="btn btn-link btn-sm p-0 d-inline-flex align-items-center gap-1" disabled={busy} onClick={() => setEditing(g)}><Pencil size={12} /> {t('users.access.edit')}</button>}
                          </>
                        ) : (
                          <span className="text-muted">{t('users.access.onApprove', { name: nodeName(m.retailerId) })}</span>
                        )}
                      </td>
                    )}
                    {tab !== 'pending' && (
                      <td className="small d-none d-lg-table-cell">
                        <div>{m.approvedBy ?? '—'}</div>
                        <div className="text-muted" style={{ fontSize: '0.8em' }}>{formatDate(m.approvedAt, i18n.language)}</div>
                      </td>
                    )}
                    <td className="text-end text-nowrap">
                      {tab === 'pending' && (
                        <>
                          <button type="button" className="btn btn-sm btn-success d-inline-flex align-items-center gap-1 me-1" disabled={locked}
                            onClick={() => run(m.id, async () => { for (const r of g.rows) await retailerMemberAction({ action: 'approve', id: r.id }); }, t('users.approved'))}>
                            {busy ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <UserCheck size={14} />} {t('users.actions.approve')}
                          </button>
                          <button type="button" className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1" disabled={locked}
                            onClick={() => run(m.id, async () => { for (const r of g.rows) await retailerMemberAction({ action: 'reject', id: r.id }); }, t('users.rejected'))}>
                            <UserX size={14} /> {t('users.actions.reject')}
                          </button>
                        </>
                      )}
                      {tab === 'approved' && (
                        <button type="button" className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1" disabled={locked}
                          title={t('users.actions.removeTitle')} onClick={() => setRemoving(g)}>
                          <UserX size={14} /> {t('users.actions.remove')}
                        </button>
                      )}
                      {tab === 'rejected' && <span className="text-muted small">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-muted mt-3 mb-0" style={{ fontSize: '0.8em' }}>{t('users.footnote')}</p>

      {editing && (
        <RetailerAccessEditor rows={editing.rows} assignable={assignable} ownRetailerId={ctx.retailerId} ownRetailerName={ctx.retailerName}
          describeError={describeError} onClose={() => setEditing(null)} onSaved={() => { setNotice({ kind: 'success', text: t('users.accessSaved') }); void load(); }} />
      )}

      <ConfirmModal open={!!removing} title={t('users.actions.removeTitle')} confirmLabel={t('users.actions.remove')} cancelLabel={t('users.access.cancel')} variant="danger" busy={busyId !== null}
        onCancel={() => setRemoving(null)}
        onConfirm={() => { const g = removing; setRemoving(null); if (!g) return; void run(g.rows[0].id, async () => { for (const r of g.rows) await retailerMemberAction({ action: 'reject', id: r.id }); }, t('users.removed')); }}>
        <p className="mb-0">{t('users.actions.removeConfirm', { name: [removing?.rows[0].firstName, removing?.rows[0].lastName].filter(Boolean).join(' ') || removing?.rows[0].email || '' })}</p>
      </ConfirmModal>
    </div>
  );
};

export default RetailerUsers;
